/**
 * Designer Workspace (Task 208 shell · 210 brief · 212 feed · 213 start/submit ·
 * 214 status lanes · 215 clarify/block).
 *
 * The creative-production workspace and role HOME for an agency `designer`.
 * Rendered by `AppLanding` whenever `isDesigner(me)` is true and none of the
 * higher command-center roles match (owner / marketing_manager /
 * account_manager / media_buyer) — for zero-, single-, and multi-client
 * designers alike, placed BEFORE the production redirect because `designer` is
 * a production-execute role. A designer with no assigned tasks sees this
 * workspace's empty state (NOT the /app/me/tasks redirect). Display-only and
 * gated upstream.
 *
 * DATA (Task 212): ASSIGNED-ONLY, read-only, from existing EFs —
 *   - `list-my-production-tasks` (Task 118): the caller's active production
 *     task assignments (RLS assignee-match; no org-wide list, no useClientScope).
 *   - `list-content-variants` (content_item_id-scoped): the selected task's
 *     content variants, whose `platform_specific` carries the existing
 *     108A/108B production brief, rendered via `productionBrief.ts`.
 * The queue groups tasks by the REAL `task_status` lifecycle (the feed has no
 * per-task deadline, so date-based grouping is intentionally not done). Task
 * selection drives the detail + brand panes; the brand kit is scoped to the
 * selected task's client and is READ-ONLY.
 *
 * ACTIONS (Task 213 + Task 215): `start` + upload+`submit` (Task 213) and
 * `request clarification` + `mark blocked` (Task 215) are all LIVE, each gated
 * by `allowedActions` per the task status and refetching the assigned-only feed
 * on success. start/submit hit the Task 101D EFs (start-production-task /
 * prepare-production-task-upload → signed PUT → submit-production-task); submit
 * REQUIRES an uploaded deliverable (no fileless submit). clarify/block hit the
 * Task 215 assignee-gated RPCs (request-production-task-clarification /
 * block-production-task): clarification is communication-only (NO status
 * change); block moves open|in_progress → blocked and the reason surfaces back
 * in the blocked lane via the assignment metadata. Task 215 ships a migration
 * (2 SECURITY DEFINER RPCs) + 2 EFs in the PR — NOT deployed/applied here.
 *
 * RTL. Scoped `.dwk-*` layout using the app's tokens.
 */

import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type {
  ListContentVariantsData,
  ListMyProductionTasksData,
  MeContext,
  MyProductionTask,
  MyProductionTaskStatus,
} from '../api/types';
import {
  blockProductionTask,
  listContentVariants,
  listMyProductionTasks,
  prepareProductionTaskUpload,
  putToSignedUploadUrl,
  requestProductionTaskClarification,
  startProductionTask,
  submitProductionTask,
} from '../api/endpoints';
import {
  BRAND_SECTIONS,
  SOON,
  TASK_LIFECYCLE,
  WORKLOAD_STATS,
  allowedActions,
  assignmentSourceLabelAr,
  avatarColor,
  awaitingReviewCount,
  computeWorkloadStats,
  disciplineLabelAr,
  distinctClientsFromTasks,
  filterTasksByClient,
  groupTasksByLane,
  initial,
  isNothingActionable,
  lifecycleIndex,
  pendingSubmitMatches,
  readBlockReason,
  resolveSelectedTask,
  taskStatusLabelAr,
  titleFromFilename,
} from './designerWorkspace.logic';
import type { PendingSubmit } from './designerWorkspace.logic';
// Task 210/212 — reuse the EXISTING Task 108A/108B production-brief contract
// for the detail pane (no parallel schema): the brief field mapping + read/
// format helpers come from the shared `productionBrief` module, which
// re-exports the `ContentVariantRow` / `ContentVariantProductionBrief` types.
import type { ContentVariantRow } from './client/productionBrief';
// Task 286F — reuse the ONE canonical content-review card (exported from
// ContentPage) for the staff production-task detail, instead of a bespoke brief
// layout. `reviewItemFromContent` adapts the loaded variants into the card shape.
import { IdeaReviewCard, reviewItemFromContent } from './client/ContentPage';
import { useProductionClientSelection } from './useProductionClientSelection';

type IconName =
  | 'search'
  | 'upload'
  | 'bell'
  | 'palette'
  | 'flame'
  | 'clock'
  | 'play'
  | 'eye'
  | 'ban'
  | 'check'
  | 'chev'
  | 'layers'
  | 'help';

const STAT_ICON: Record<string, IconName> = {
  total: 'layers',
  open: 'clock',
  in_progress: 'play',
  submitted: 'eye',
  blocked: 'ban',
};

function Icon({ name, className }: { readonly name: IconName; readonly className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      aria-hidden="true"
    >
      {iconBody(name)}
    </svg>
  );
}

function iconBody(name: IconName): ReactNode {
  switch (name) {
    case 'search':
      return (
        <>
          <circle cx="11" cy="11" r="7" />
          <path d="m21 21-4.3-4.3" strokeLinecap="round" />
        </>
      );
    case 'upload':
      return <path d="M12 16V4M7 9l5-5 5 5M5 20h14" strokeLinecap="round" strokeLinejoin="round" />;
    case 'bell':
      return (
        <path
          d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9M13.7 21a2 2 0 0 1-3.4 0"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );
    case 'palette':
      return (
        <path
          d="M12 3a9 9 0 1 0 0 18 3 3 0 0 0 2-5 3 3 0 0 1 2-5h1a4 4 0 0 0 4-4 9 9 0 0 0-9-4z"
          strokeLinejoin="round"
        />
      );
    case 'flame':
      return (
        <path
          d="M12 2s5 4 5 9a5 5 0 0 1-10 0c0-1.5.6-2.7 1.3-3.6C9 9 9.5 10 10.5 10c0-2 .5-5 1.5-8z"
          strokeLinejoin="round"
        />
      );
    case 'clock':
      return (
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" strokeLinecap="round" />
        </>
      );
    case 'play':
      return <path d="M7 4v16l13-8z" strokeLinejoin="round" />;
    case 'eye':
      return (
        <>
          <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" strokeLinejoin="round" />
          <circle cx="12" cy="12" r="3" />
        </>
      );
    case 'ban':
      return (
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M5.6 5.6l12.8 12.8" strokeLinecap="round" />
        </>
      );
    case 'check':
      return (
        <path
          d="M9 11l3 3 8-8M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );
    case 'chev':
      return <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />;
    case 'layers':
      return (
        <path
          d="M12 2l9 5-9 5-9-5zM3 12l9 5 9-5M3 17l9 5 9-5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );
    case 'help':
      return (
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.8.4-1 .9-1 1.7M12 17h.01" strokeLinecap="round" />
        </>
      );
    default:
      return null;
  }
}

type TasksState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly tasks: ReadonlyArray<MyProductionTask> }
  | { readonly status: 'error' };

type VariantState =
  | { readonly status: 'idle' }
  | { readonly status: 'no_item' }
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready';
      // The content item these variants belong to — guards against rendering a
      // previous task's brief for one frame while the fetch effect catches up.
      readonly contentItemId: string;
      readonly variants: ReadonlyArray<ContentVariantRow>;
    }
  | { readonly status: 'error' };

/** Extract a user-facing Arabic message from a mutation error. */
function actionErrorMessage(err: unknown): string {
  if (err instanceof Error && err.message.trim().length > 0) return err.message;
  return 'تعذّر تنفيذ الإجراء. حاول مرّة أخرى.';
}

interface DesignerWorkspaceProps {
  readonly me: MeContext;
}

export function DesignerWorkspace({ me }: DesignerWorkspaceProps) {
  const designerName = me.profile?.full_name ?? me.profile?.email ?? 'المصمم';

  const [tasksState, setTasksState] = useState<TasksState>({ status: 'loading' });
  const [tasksReloadKey, setTasksReloadKey] = useState(0);
  const { selectedClientId: clientFilter, setSelectedClientId: setClientFilter } =
    useProductionClientSelection(me.client_assignments);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

  const [variantState, setVariantState] = useState<VariantState>({ status: 'idle' });
  const [variantReloadKey, setVariantReloadKey] = useState(0);

  // ── Task 213 — production-task action state (start · upload+submit). ──
  const [actionInFlight, setActionInFlight] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [stagedFile, setStagedFile] = useState<File | null>(null);
  const [submitTitle, setSubmitTitle] = useState('');
  // 101D Codex P2 retry guard: preserve a prepared storage_key across submit
  // retries so (task_id, storage_key) idempotency holds and the bucket is not
  // left with an orphan object if PUT succeeds but submit fails.
  const [pendingSubmit, setPendingSubmit] = useState<PendingSubmit | null>(null);

  // Task 215 — clarify / block inline form (which action's reason input is
  // open, and its text). Both are assignee-gated mutations on the selected task.
  const [actionForm, setActionForm] = useState<'clarify' | 'block' | null>(null);
  const [actionText, setActionText] = useState('');

  // ── Assigned-only task feed (RLS assignee-match; NOT useClientScope). ──
  useEffect(() => {
    const ctrl = new AbortController();
    // Keep the current list visible during a post-mutation REFETCH (only blank
    // to 'loading' on the first load / after an error). This keeps the selected
    // task stable so the action success banner survives the reload instead of
    // being wiped by the reset-on-select effect.
    setTasksState((prev) => (prev.status === 'ready' ? prev : { status: 'loading' }));
    listMyProductionTasks({ signal: ctrl.signal })
      .then((data: ListMyProductionTasksData) => {
        if (ctrl.signal.aborted) return;
        setTasksState({ status: 'ready', tasks: data.tasks });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setTasksState({ status: 'error' });
      });
    return () => ctrl.abort();
  }, [tasksReloadKey]);

  const allTasks = tasksState.status === 'ready' ? tasksState.tasks : [];
  const clientOptions = distinctClientsFromTasks(allTasks);
  // A selected assigned client remains authoritative even with zero tasks. Falling
  // back to the full feed here would expose unrelated clients in the same queue.
  const activeClientFilter = clientFilter;
  const filteredTasks = filterTasksByClient(allTasks, activeClientFilter);
  const lanes = groupTasksByLane(filteredTasks);
  const selectedTask = resolveSelectedTask(filteredTasks, selectedTaskId);
  const counts =
    tasksState.status === 'ready'
      ? activeClientFilter
        ? computeWorkloadStats(filteredTasks)
        : computeWorkloadStats(allTasks)
      : null;

  const selectedAssignmentId = selectedTask?.assignment_id ?? null;
  const selectedContentItemId = selectedTask?.content_item?.id ?? null;

  // Only treat the fetched variants as current when they belong to the selected
  // task's content item — otherwise show loading until the effect catches up
  // (prevents a one-frame flash of the previous task's brief).
  const briefState: VariantState =
    variantState.status === 'ready' && variantState.contentItemId !== selectedContentItemId
      ? { status: 'loading' }
      : variantState;

  // ── Selected task's content variants → the 108A/108B brief. ──
  useEffect(() => {
    if (!selectedAssignmentId) {
      setVariantState({ status: 'idle' });
      return;
    }
    if (!selectedContentItemId) {
      setVariantState({ status: 'no_item' });
      return;
    }
    const ctrl = new AbortController();
    setVariantState({ status: 'loading' });
    listContentVariants(selectedContentItemId, { signal: ctrl.signal })
      .then((data: ListContentVariantsData) => {
        if (ctrl.signal.aborted) return;
        setVariantState({
          status: 'ready',
          contentItemId: selectedContentItemId,
          variants: data.content_variants,
        });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setVariantState({ status: 'error' });
      });
    return () => ctrl.abort();
  }, [selectedAssignmentId, selectedContentItemId, variantReloadKey]);

  // Reset the action form whenever the selected task changes (explicit click,
  // client-filter change, or fallback selection) so staged files / messages
  // never leak across tasks.
  useEffect(() => {
    setActionError(null);
    setActionSuccess(null);
    setStagedFile(null);
    setSubmitTitle('');
    setPendingSubmit(null);
    setActionForm(null);
    setActionText('');
  }, [selectedAssignmentId]);

  function selectTask(assignmentId: string) {
    setSelectedTaskId(assignmentId);
  }

  async function handleStart(taskId: string) {
    if (actionInFlight) return;
    setActionError(null);
    setActionSuccess(null);
    setActionInFlight(true);
    try {
      const data = await startProductionTask({ input: { task_id: taskId } });
      setActionSuccess(
        data.task_already_started
          ? 'المهمة قيد التنفيذ بالفعل.'
          : 'تم بدء المهمة — الحالة الآن: قيد التنفيذ.',
      );
      setTasksReloadKey((n) => n + 1); // refetch the assigned-only feed
    } catch (err: unknown) {
      setActionError(actionErrorMessage(err));
    } finally {
      setActionInFlight(false);
    }
  }

  function onPickFile(file: File | null) {
    setActionError(null);
    setActionSuccess(null);
    setPendingSubmit(null); // a new pick invalidates any previously-prepared key
    setStagedFile(file);
    setSubmitTitle(file ? titleFromFilename(file.name) : '');
  }

  async function handleSubmit(taskId: string) {
    if (actionInFlight || !stagedFile) return;
    const title = submitTitle.trim();
    if (title.length === 0) {
      setActionError('أدخل عنواناً للتسليم.');
      return;
    }
    setActionError(null);
    setActionSuccess(null);
    setActionInFlight(true);
    try {
      const file = stagedFile;
      const mime = (file.type || '').toLowerCase() || 'application/octet-stream';
      // Reuse a previously-prepared storage_key when retrying the SAME file
      // (101D Codex P2) — submit is idempotent on (task_id, storage_key), so a
      // retry after a failed submit must not orphan a freshly-PUT object.
      const reuse = pendingSubmitMatches(pendingSubmit, {
        taskId,
        fileName: file.name,
        fileSize: file.size,
        fileLastModified: file.lastModified,
      })
        ? pendingSubmit
        : null;
      let storageKey: string;
      if (reuse) {
        storageKey = reuse.storageKey;
      } else {
        // 1) server gates + mints a signed PUT URL + canonical storage_key.
        const prepared = await prepareProductionTaskUpload({
          input: { task_id: taskId, filename: file.name, mime_type: mime, byte_size: file.size },
        });
        // 2) direct browser PUT into the private kmi-task-files bucket.
        await putToSignedUploadUrl({ url: prepared.upload_url, file, contentType: mime });
        storageKey = prepared.storage_key;
        // Stash BEFORE submit so a submit failure leaves a recoverable pointer.
        setPendingSubmit({
          taskId,
          storageKey,
          fileName: file.name,
          fileSize: file.size,
          fileLastModified: file.lastModified,
        });
      }
      // 3) atomic DB write + flip task → 'submitted'. Idempotent on
      //    (task_id, storage_key).
      const data = await submitProductionTask({
        input: { task_id: taskId, storage_key: storageKey, title, mime_type: mime },
      });
      setPendingSubmit(null);
      setStagedFile(null);
      setSubmitTitle('');
      setActionSuccess(
        data.task_already_submitted
          ? 'تم تسليم هذا الملف مسبقاً.'
          : 'تم إرسال التسليم للمراجعة — الحالة الآن: بانتظار المراجعة.',
      );
      setTasksReloadKey((n) => n + 1);
    } catch (err: unknown) {
      setActionError(actionErrorMessage(err));
    } finally {
      setActionInFlight(false);
    }
  }

  function openActionForm(form: 'clarify' | 'block') {
    setActionError(null);
    setActionSuccess(null);
    setActionForm(form);
    setActionText('');
  }

  function cancelActionForm() {
    setActionForm(null);
    setActionText('');
  }

  // Task 215 — submit the open clarify/block form for the selected task.
  // clarify is communication-only (no status change); block flips the task to
  // 'blocked' (→ Task 214 blocked lane). Both refetch the assigned-only feed.
  async function submitActionForm(taskId: string) {
    if (actionInFlight || actionForm === null) return;
    const text = actionText.trim();
    if (text.length === 0) {
      setActionError(actionForm === 'block' ? 'أدخل سبب التعليق.' : 'أدخل رسالة التوضيح.');
      return;
    }
    setActionError(null);
    setActionSuccess(null);
    setActionInFlight(true);
    try {
      if (actionForm === 'block') {
        await blockProductionTask({ input: { task_id: taskId, reason: text } });
        setActionSuccess('تم تعليم المهمة كمحظورة وإبلاغ المسؤولين — الحالة الآن: محظورة.');
      } else {
        await requestProductionTaskClarification({ input: { task_id: taskId, message: text } });
        setActionSuccess('أُرسل طلب التوضيح إلى مسؤولي العميل.');
      }
      setActionForm(null);
      setActionText('');
      setTasksReloadKey((n) => n + 1);
    } catch (err: unknown) {
      setActionError(actionErrorMessage(err));
    } finally {
      setActionInFlight(false);
    }
  }

  return (
    <div className="dwk" dir="rtl">
      {/* ── Top bar ─────────────────────────────────────────────── */}
      <div className="dwk-top">
        <div className="dwk-greet">
          <h1 className="h-page">
            مساحة{' '}
            <em className="serif italic" style={{ color: 'var(--tangerine)' }}>
              التصميم
            </em>
          </h1>
          <p className="dwk-sub">
            أهلاً {designerName} — مهامك المسندة وموجزات الإنتاج وأدلّة العلامات في مكان واحد.
          </p>
        </div>
        <div className="dwk-search">
          <input type="search" placeholder="ابحث في مهامك أو عملائك…" aria-label="بحث" />
          <Icon name="search" />
        </div>
        <button
          type="button"
          className="dwk-upload"
          disabled
          title="قيد الربط — رفع التصاميم غير مفعّل بعد"
        >
          <Icon name="upload" />
          رفع تصميم
        </button>
        <span className="dwk-iconbtn" aria-hidden="true">
          <Icon name="bell" />
        </span>
      </div>

      {/* ── Workload (real counts) ──────────────────────────────── */}
      <div className="dwk-toolbar">
        <div className="dwk-wstrip">
          {WORKLOAD_STATS.map((s) => (
            <div key={s.key} className="dwk-wstat">
              <span className={`dwk-wi ${s.tone}`}>
                <Icon name={STAT_ICON[s.key] ?? 'clock'} />
              </span>
              <span>
                <span className="dwk-wv">{counts ? counts[s.key] : SOON}</span>
                <span className="dwk-wl">{s.label}</span>
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* ── 3-pane studio ───────────────────────────────────────── */}
      <div className="dwk-studio">
        {/* QUEUE — real assigned tasks grouped by status */}
        <section className="dwk-pane">
          <div className="dwk-pane-head">
            <span className="dwk-ph-t">
              طابور <em>المهام</em>
            </span>
            <span className="dwk-ph-c">{counts ? counts.total : '—'}</span>
          </div>
          <div className="dwk-queue">
            {tasksState.status === 'loading' && (
              <div className="occ-empty sm">جارٍ تحميل مهامك المسندة…</div>
            )}
            {tasksState.status === 'error' && (
              <div className="occ-empty sm">
                تعذّر تحميل المهام.{' '}
                <button
                  type="button"
                  className="dwk-retry"
                  onClick={() => setTasksReloadKey((n) => n + 1)}
                >
                  إعادة المحاولة
                </button>
              </div>
            )}
            {tasksState.status === 'ready' && allTasks.length === 0 && (
              <div className="dwk-queue-note occ-empty sm">
                لا توجد مهام مُسنَدة إليك حالياً. ستظهر هنا فور تعيينك على مهمة إنتاج — تلقائياً بعد
                اعتماد العميل أو يدوياً من الإدارة.
              </div>
            )}
            {tasksState.status === 'ready' && allTasks.length > 0 && (
              <>
                {clientOptions.length > 1 && (
                  <div className="dwk-filters" role="group" aria-label="تصفية حسب العميل">
                    <button
                      type="button"
                      className={`dwk-fbtn${activeClientFilter === null ? ' active' : ''}`}
                      aria-pressed={activeClientFilter === null}
                      onClick={() => setClientFilter(null)}
                    >
                      الكل
                    </button>
                    {clientOptions.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        className={`dwk-fbtn${activeClientFilter === c.id ? ' active' : ''}`}
                        aria-pressed={activeClientFilter === c.id}
                        onClick={() => setClientFilter(c.id)}
                      >
                        {c.name} <span className="dwk-gc">{c.count}</span>
                      </button>
                    ))}
                  </div>
                )}
                {filteredTasks.length === 0 ? (
                  <div className="occ-empty sm">لا توجد مهام لهذا العميل.</div>
                ) : (
                  <>
                    {isNothingActionable(filteredTasks) && (
                      <div className="dwk-noaction occ-empty sm">
                        لا يوجد عمل مطلوب منك الآن. لديك {awaitingReviewCount(filteredTasks)} تسليم
                        بانتظار المراجعة.
                      </div>
                    )}
                    {lanes.map((lane) =>
                      lane.tasks.length > 0 ? (
                        <div
                          key={lane.key}
                          className={`dwk-lane ${lane.key}${lane.deemphasized ? ' muted' : ''}`}
                        >
                          <div className="dwk-lane-head">
                            {lane.label} <span className="dwk-gc">{lane.tasks.length}</span>
                          </div>
                          {lane.tasks.map((t) => (
                            <TaskCard
                              key={t.assignment_id}
                              task={t}
                              selected={selectedTask?.assignment_id === t.assignment_id}
                              deemphasized={lane.deemphasized}
                              onSelect={() => selectTask(t.assignment_id)}
                            />
                          ))}
                        </div>
                      ) : null,
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </section>

        {/* DETAIL — selected task + its production brief */}
        <section className="dwk-pane">
          <div className="dwk-pane-head">
            <span className="dwk-ph-t">
              تفاصيل <em>المهمة</em>
            </span>
          </div>
          {selectedTask ? (
            <div className="dwk-detail">
              <div className="dwk-dt-hero">
                <div className="dwk-crumb">
                  <span
                    className="dwk-dc-av"
                    style={{
                      background: avatarColor(
                        selectedTask.client?.id ?? selectedTask.assignment_id,
                      ),
                    }}
                  >
                    {initial(selectedTask.client?.name ?? '؟')}
                  </span>
                  <span className="dwk-dc-nm">{selectedTask.client?.name ?? 'عميل غير متاح'}</span>
                  <span className="occ-badge">{taskStatusLabelAr(selectedTask.task_status)}</span>
                </div>
                <div className="dwk-dt-title">
                  {selectedTask.content_item?.title ?? 'مهمة بدون عنوان'}
                </div>
                <p className="dwk-dt-hint">
                  {selectedTask.content_item?.discipline
                    ? `${disciplineLabelAr(selectedTask.content_item.discipline)} — `
                    : ''}
                  {assignmentSourceLabelAr(selectedTask.assignment_metadata)} · موجز الإنتاج
                  (108A/108B) للقراءة فقط.
                </p>
              </div>
              <Lifecycle status={selectedTask.task_status} />
              {selectedTask.task_status === 'blocked' &&
                readBlockReason(selectedTask.assignment_metadata) !== null && (
                  <div className="dwk-block-reason">
                    <span className="dwk-block-reason-t">سبب التعليق</span>
                    <span className="dwk-block-reason-v">
                      {readBlockReason(selectedTask.assignment_metadata)}
                    </span>
                  </div>
                )}
              <div className="dwk-dt-body">
                <VariantBrief
                  state={briefState}
                  contentTitle={selectedTask.content_item?.title ?? 'مهمة بدون عنوان'}
                  onRetry={() => setVariantReloadKey((n) => n + 1)}
                />
              </div>
              <div className="dwk-actionbar">
                <ActionBar
                  task={selectedTask}
                  inFlight={actionInFlight}
                  stagedFile={stagedFile}
                  submitTitle={submitTitle}
                  actionForm={actionForm}
                  actionText={actionText}
                  onStart={() => handleStart(selectedTask.task_id)}
                  onPickFile={onPickFile}
                  onChangeTitle={setSubmitTitle}
                  onSubmit={() => handleSubmit(selectedTask.task_id)}
                  onOpenForm={openActionForm}
                  onChangeActionText={setActionText}
                  onSubmitForm={() => submitActionForm(selectedTask.task_id)}
                  onCancelForm={cancelActionForm}
                />
                {actionError !== null && <div className="dwk-act-msg err">{actionError}</div>}
                {actionSuccess !== null && <div className="dwk-act-msg ok">{actionSuccess}</div>}
              </div>
            </div>
          ) : (
            <div className="occ-empty">اختر مهمة من الطابور لعرض تفاصيلها وموجز الإنتاج.</div>
          )}
        </section>

        {/* BRAND KIT — read-only, scoped to the selected task's client */}
        <section className="dwk-pane">
          <div className="dwk-pane-head">
            <span className="dwk-ph-t">
              دليل <em>العلامة</em>
            </span>
            <span className="dwk-ph-c">للقراءة فقط</span>
          </div>
          {selectedTask?.client ? (
            <div className="dwk-brand">
              <div className="dwk-bk-hero">
                <span
                  className="dwk-bk-logo"
                  style={{ background: avatarColor(selectedTask.client.id) }}
                >
                  {initial(selectedTask.client.name)}
                </span>
                <span>
                  <span className="dwk-bk-name">{selectedTask.client.name}</span>
                  <span className="dwk-bk-scope">دليل علامة هذا العميل فقط</span>
                </span>
              </div>
              <div className="dwk-bk-body">
                {BRAND_SECTIONS.map((s) => (
                  <div key={s.key} className="dwk-bsec">
                    <div className="dwk-bsec-t">{s.label}</div>
                    <div className="dwk-bsec-hint">
                      {s.hint} — <span className="dwk-soon">قيد الربط</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="occ-empty">يظهر دليل العلامة هنا عند اختيار مهمة من الطابور.</div>
          )}
        </section>
      </div>
    </div>
  );
}

interface TaskCardProps {
  readonly task: MyProductionTask;
  readonly selected: boolean;
  readonly deemphasized?: boolean;
  readonly onSelect: () => void;
}

function TaskCard({ task, selected, deemphasized = false, onSelect }: TaskCardProps) {
  const clientName = task.client?.name ?? 'عميل غير متاح';
  const title = task.content_item?.title ?? 'مهمة بدون عنوان';
  const discipline = task.content_item?.discipline;
  return (
    <button
      type="button"
      className={`dwk-task${selected ? ' sel' : ''}${deemphasized ? ' muted' : ''}`}
      onClick={onSelect}
      aria-pressed={selected}
    >
      <span
        className="dwk-task-av"
        style={{ background: avatarColor(task.client?.id ?? task.assignment_id) }}
      >
        {initial(clientName)}
      </span>
      <span className="dwk-task-main">
        <span className="dwk-task-client">{clientName}</span>
        <span className="dwk-task-title">{title}</span>
        <span className="dwk-task-meta">
          {discipline ? <span className="dwk-chip">{disciplineLabelAr(discipline)}</span> : null}
          <span className="dwk-chip soft">{assignmentSourceLabelAr(task.assignment_metadata)}</span>
        </span>
      </span>
      <Icon name="chev" className="dwk-cl-chev" />
    </button>
  );
}

interface VariantBriefProps {
  readonly state: VariantState;
  readonly contentTitle: string;
  readonly onRetry: () => void;
}

/**
 * Task 286F — renders the selected task's content + production brief through the ONE
 * shared IdeaReviewCard (saved mode = the internal, brief-first presentation), so the
 * designer sees the SAME canonical card as internal review / saved / client review —
 * no bespoke/duplicated brief layout. The card owns variant selection (its own
 * platform chips), the design/production brief, the recommended canvas sizes, the
 * caption/public copy, and the asset/media region. Honest empty states are kept for:
 * task has no readable content item · still loading · error · task has no variant.
 */
function VariantBrief({ state, contentTitle, onRetry }: VariantBriefProps) {
  if (state.status === 'no_item') {
    return (
      <div className="occ-empty sm">هذه المهمة بلا عنصر محتوى مقروء (قد يكون وصولك محدوداً).</div>
    );
  }
  if (state.status === 'idle' || state.status === 'loading') {
    return <div className="occ-empty sm">جارٍ تحميل موجز الإنتاج…</div>;
  }
  if (state.status === 'error') {
    return (
      <div className="occ-empty sm">
        تعذّر تحميل موجز الإنتاج.{' '}
        <button type="button" className="dwk-retry" onClick={onRetry}>
          إعادة المحاولة
        </button>
      </div>
    );
  }
  const variants = state.variants;
  if (variants.length === 0) {
    return <div className="occ-empty sm">لا توجد نسخة محتوى مرتبطة بهذه المهمة بعد.</div>;
  }
  // Task 286F — adapt the loaded variants into the canonical card shape (read-only —
  // no edit callbacks). The idea body isn't loaded on the task route, so the concept
  // band derives the hook + visual direction from the brief (variants[0].brief).
  const reviewItem = reviewItemFromContent({
    key: state.contentItemId,
    title: contentTitle,
    body: null,
    variants,
  });
  return (
    // Task 286F — key by the content item so switching tasks REMOUNTS the card,
    // resetting its internal selected-variant index (the old VariantBrief reset its
    // own `selectedVariantId` on task change; the shared card owns that state now).
    <IdeaReviewCard
      key={reviewItem.key}
      item={reviewItem}
      idx={0}
      mode="saved"
      mediaSlot={() => (
        <div className="occ-empty sm">الأصل قيد الإنتاج — يُرفع عند إنجاز المهمة.</div>
      )}
    />
  );
}

interface ActionBarProps {
  readonly task: MyProductionTask;
  readonly inFlight: boolean;
  readonly stagedFile: File | null;
  readonly submitTitle: string;
  readonly actionForm: 'clarify' | 'block' | null;
  readonly actionText: string;
  readonly onStart: () => void;
  readonly onPickFile: (file: File | null) => void;
  readonly onChangeTitle: (title: string) => void;
  readonly onSubmit: () => void;
  readonly onOpenForm: (form: 'clarify' | 'block') => void;
  readonly onChangeActionText: (text: string) => void;
  readonly onSubmitForm: () => void;
  readonly onCancelForm: () => void;
}

/**
 * The designer task action bar. All gated by `allowedActions` per task status:
 * `start` + upload/`submit` (Task 213) and — Task 215 — `clarify` (a question,
 * any active status) + `block` (open/in_progress, requires a reason). clarify /
 * block each open an inline required-text form. Submit REQUIRES an attached
 * deliverable — there is no fileless submit. A submitted task is read-only.
 */
function ActionBar({
  task,
  inFlight,
  stagedFile,
  submitTitle,
  actionForm,
  actionText,
  onStart,
  onPickFile,
  onChangeTitle,
  onSubmit,
  onOpenForm,
  onChangeActionText,
  onSubmitForm,
  onCancelForm,
}: ActionBarProps) {
  const avail = allowedActions(task.task_status);
  // Task 214 — a submitted task is done and awaiting the REVIEWER; the designer
  // has nothing to act on, so show a read-only status note instead of the
  // (disabled) deferred-action buttons.
  if (task.task_status === 'submitted') {
    return (
      <div className="dwk-actions">
        <span className="dwk-submitted-note">تم التسليم — بانتظار المراجعة</span>
      </div>
    );
  }
  const fileInputId = `dwk-file-${task.assignment_id}`;
  return (
    <div className="dwk-actions">
      {avail.start && (
        <button type="button" className="dwk-act primary" disabled={inFlight} onClick={onStart}>
          {inFlight ? 'جارٍ…' : 'بدء المهمة'}
        </button>
      )}
      {avail.submit && (
        <div className="dwk-submit">
          <input
            id={fileInputId}
            type="file"
            className="dwk-file-input"
            onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
          />
          <label htmlFor={fileInputId} className="dwk-act dark">
            {stagedFile ? stagedFile.name : 'إرفاق ملف التسليم'}
          </label>
          {stagedFile && (
            <>
              <input
                type="text"
                className="dwk-submit-title"
                value={submitTitle}
                onChange={(e) => onChangeTitle(e.target.value)}
                placeholder="عنوان التسليم"
                aria-label="عنوان التسليم"
              />
              <button
                type="button"
                className="dwk-act primary"
                disabled={inFlight || submitTitle.trim().length === 0}
                onClick={onSubmit}
              >
                {inFlight ? 'جارٍ الإرسال…' : 'إرسال للمراجعة'}
              </button>
            </>
          )}
        </div>
      )}
      {avail.clarify && (
        <button
          type="button"
          className={`dwk-act ghost${actionForm === 'clarify' ? ' active' : ''}`}
          disabled={inFlight}
          aria-pressed={actionForm === 'clarify'}
          onClick={() => onOpenForm('clarify')}
        >
          طلب توضيح
        </button>
      )}
      {avail.block && (
        <button
          type="button"
          className={`dwk-act danger${actionForm === 'block' ? ' active' : ''}`}
          disabled={inFlight}
          aria-pressed={actionForm === 'block'}
          onClick={() => onOpenForm('block')}
        >
          تعليم محظور
        </button>
      )}
      {actionForm !== null && (
        <div className="dwk-action-form">
          <textarea
            className="dwk-action-text"
            value={actionText}
            onChange={(e) => onChangeActionText(e.target.value)}
            placeholder={actionForm === 'block' ? 'سبب التعليق…' : 'ما الذي تحتاج توضيحه؟'}
            aria-label={actionForm === 'block' ? 'سبب التعليق' : 'رسالة التوضيح'}
            rows={3}
          />
          <div className="dwk-action-form-btns">
            <button
              type="button"
              className="dwk-act primary"
              disabled={inFlight || actionText.trim().length === 0}
              onClick={onSubmitForm}
            >
              {inFlight ? 'جارٍ…' : actionForm === 'block' ? 'تأكيد التعليق' : 'إرسال الطلب'}
            </button>
            <button
              type="button"
              className="dwk-act ghost"
              disabled={inFlight}
              onClick={onCancelForm}
            >
              إلغاء
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Read-only state-transition display: the linear designer lifecycle
 * (جديدة → قيد التنفيذ → بانتظار المراجعة) with the current step highlighted.
 * `blocked` is a side state shown as a distinct marker (not a linear step).
 */
function Lifecycle({ status }: { readonly status: MyProductionTaskStatus }) {
  const current = lifecycleIndex(status);
  return (
    <div className="dwk-lifecycle" aria-label="حالة المهمة">
      {TASK_LIFECYCLE.map((step, i) => {
        const stepState =
          current === -1 ? '' : i < current ? ' done' : i === current ? ' active' : '';
        return (
          <span key={step.key} className={`dwk-lc-step${stepState}`}>
            {step.label}
          </span>
        );
      })}
      {status === 'blocked' && <span className="dwk-lc-step blocked active">محظورة</span>}
    </div>
  );
}
